import type { FC } from "react";
import { useRef, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import { Scrollbar } from "src/components/scrollbar";
import { usePathname } from "src/hooks/use-pathname";
import { paths } from "src/paths";
import type { NavColor } from "src/types/settings";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import useWhiteLabelConfig from "src/contexts/snug/config-context";

import type { Section } from "../config";
import { SideNavSection } from "./side-nav-section";
import useUserDataProvider from "src/contexts/snug/userData-context";
import { NetworkSwitch } from "../network-switch";

const SIDE_NAV_WIDTH: number = 280;

const SNUG_LOGO_URL =
  "https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png";

const useCssVars = (color: NavColor): Record<string, string> => {
  const theme = useTheme();

  return useMemo((): Record<string, string> => {
    switch (color) {
      case "blend-in":
        if (theme.palette.mode === "dark") {
          return {
            "--nav-bg": theme.palette.background.default,
            "--nav-color": theme.palette.neutral[100],
            "--nav-border-color": theme.palette.neutral[700],
            "--nav-logo-border": theme.palette.neutral[700],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[600],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[700],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[400],
          };
        } else {
          return {
            "--nav-bg": theme.palette.background.default,
            "--nav-color": theme.palette.text.primary,
            "--nav-border-color": theme.palette.neutral[100],
            "--nav-logo-border": theme.palette.neutral[100],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": theme.palette.action.hover,
            "--nav-item-active-bg": theme.palette.action.selected,
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[400],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[400],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[900],
          };
        }

      case "discreet":
        if (theme.palette.mode === "dark") {
          return {
            "--nav-bg": theme.palette.neutral[900],
            "--nav-color": theme.palette.neutral[100],
            "--nav-border-color": theme.palette.neutral[700],
            "--nav-logo-border": theme.palette.neutral[700],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[600],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[700],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[400],
          };
        } else {
          return {
            "--nav-bg": "#f5f5f5",
            "--nav-color": theme.palette.text.primary,
            "--nav-border-color": theme.palette.divider,
            "--nav-logo-border": theme.palette.neutral[200],
            "--nav-section-title-color": theme.palette.neutral[500],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": theme.palette.action.hover,
            "--nav-item-active-bg": theme.palette.action.selected,
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[400],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[400],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[900],
          };
        }

      case "evident":
        if (theme.palette.mode === "dark") {
          return {
            "--nav-bg": theme.palette.neutral[800],
            "--nav-color": theme.palette.common.white,
            "--nav-border-color": "transparent",
            "--nav-logo-border": theme.palette.neutral[700],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-color": theme.palette.common.white,
            "--nav-item-disabled-color": theme.palette.neutral[500],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[500],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[400],
          };
        } else {
          return {
            "--nav-bg": "#f5f5f5",
            "--nav-color": theme.palette.text.primary,
            "--nav-border-color": theme.palette.divider,
            "--nav-logo-border": theme.palette.neutral[200],
            "--nav-section-title-color": theme.palette.neutral[500],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": theme.palette.action.hover,
            "--nav-item-active-bg": theme.palette.action.selected,
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[400],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[400],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[900],
          };
        }

      default:
        return {};
    }
  }, [theme, color]);
};

interface SideNavProps {
  color?: NavColor;
  sections?: Section[];
}

export const SideNav: FC<SideNavProps> = (props) => {
  const { color = "evident", sections = [] } = props;
  const pathname = usePathname();
  const cssVars = useCssVars(color);

  const { email_sender, wl_professional_group } = useWhiteLabelConfig();
  const { isFirmAdmin, professionalGroup, isPaidTier } = useUserDataProvider();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const imageRef = useRef<HTMLImageElement>(null);

  // The dashboard chrome only shows the firm's logo on white-labeled dashboards;
  // standard dashboards default to the Snug logo at its natural size.
  const isWhiteLabeled = !!wl_professional_group?.dashboard_wl_on;
  const logoSrc = isWhiteLabeled
    ? wl_professional_group?.app_logo || undefined
    : SNUG_LOGO_URL;
  const logoMultiplier = isWhiteLabeled
    ? wl_professional_group?.total_app_logo_multiplier
    : 1;

  useEffect(() => {
    const handleResize = () => {
      if (imageRef.current && logoSrc && logoMultiplier) {
        const { naturalWidth, naturalHeight } = imageRef.current;
        const aspectRatio = naturalWidth / naturalHeight;
        let maxWidth = 170;
        let maxHeight = 30;

        // Adjust maxWidth for aspect ratios
        if (aspectRatio < 2.9) {
          maxHeight = 48;
        }
        if (aspectRatio >= 2.9 && aspectRatio <= 4) {
          maxHeight = 40;
        }
        if (aspectRatio > 4) {
          maxHeight = 35;
        }

        if (naturalWidth > maxWidth || naturalHeight > maxHeight) {
          if (maxWidth / aspectRatio < maxHeight) {
            setDimensions({ width: maxWidth * logoMultiplier, height: maxWidth / aspectRatio * logoMultiplier });
          } else {
            setDimensions({
              width: maxHeight * aspectRatio * logoMultiplier,
              height: maxHeight * logoMultiplier,
            });
          }
        } else {
          setDimensions({ width: naturalWidth * logoMultiplier, height: naturalHeight * logoMultiplier });
        }
      }
    };

    const img = imageRef.current;

    if (img && logoSrc) {
      img.onload = handleResize;
      img.src = logoSrc;
    }

    return () => {
      if (img) {
        img.onload = null;
      }
    };
  }, [logoSrc, logoMultiplier]);

  return (
    <Drawer
      anchor="left"
      open
      PaperProps={{
        sx: {
          ...cssVars,
          backgroundColor: "var(--nav-bg)",
          borderRightColor: "#e7e7e7",
          borderRightStyle: "solid",
          borderRightWidth: 1,
          color: "var(--nav-color)",
          width: SIDE_NAV_WIDTH,
        },
      }}
      variant="permanent"
    >
      <Scrollbar
        sx={{
          height: "100%",
          "& .simplebar-content": {
            height: "100%",
          },
          "& .simplebar-scrollbar:before": {
            background: "var(--nav-scrollbar-color)",
          },
        }}
      >
        <Stack sx={{ height: "100%" }}>
          <Stack
            alignItems="center"
            justifyContent="start"
            direction="row"
            spacing={2}
            sx={{ px: 3, pt: 3, pb: 3 }}
          >
            <Box>
              <img
                ref={imageRef}
                src={logoSrc}
                style={{ width: dimensions.width, height: dimensions.height }}
              />
            </Box>
          </Stack>
          <Box sx={{ px: 2.5, pb: 2, "&:empty": { display: "none" } }}>
            <NetworkSwitch />
          </Box>
          <Stack
            component="nav"
            spacing={1}
            sx={{
              flexGrow: 1,
              px: 2.5,
            }}
          >
            {sections.map((section, index) => (
              <SideNavSection
                items={section.items}
                key={index}
                pathname={pathname}
                subheader={section.subheader}
              />
            ))}
          </Stack>
          <Box sx={{ p: 3 }}>
            
            {(isFirmAdmin || !wl_professional_group?.dashboard_wl_on) && (
              <Typography sx={{ mb: 0 }} variant="subtitle2">
                <a
                  href="https://intercom.help/getsnug/en"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "inherit", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 16 }} />
                  Help Center
                </a>
              </Typography>
            )}
            {/* Hidden on staging so it doesn't appear in unbranded demo videos */}
            {process.env.REACT_APP_SNUG_ENV !== "STAGING" && (
              <Typography color="neutral.400" sx={{ mb: 1 }} variant="body2">
                {email_sender}
              </Typography>
            )}
            {!isPaidTier && (
              <Button
                startIcon={<AutoAwesomeIcon />}
                variant="contained"
                fullWidth
                href={paths.dashboard.upgrade}
              >
                Complete Onboarding
              </Button>
            )}
            {isPaidTier && !professionalGroup?.onboarding_completed_at && (
              <Button
                startIcon={<AutoAwesomeIcon />}
                variant="contained"
                fullWidth
                href={paths.dashboard.upgrade}
              >
                Complete Setup
              </Button>
            )}
          </Box>
        </Stack>
      </Scrollbar>
    </Drawer>
  );
};

SideNav.propTypes = {
  color: PropTypes.oneOf<NavColor>(["blend-in", "discreet", "evident"]),
  sections: PropTypes.array,
};
